import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/atoms'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import logoSecondLane from '@/assets/logoSecondLane.svg'
import signupRightPanel from '@/assets/signupRightPanel.svg'
import world2Icon from '@/assets/world2.svg'
import type { AttestationScreenBaseProps } from './AttestationScreenBase'

export const AttestationScreenDesktop = ({
  formData,
  loading,
  error,
  fieldErrors,
  saveState,
  onFormDataChange,
  onSubmit,
}: AttestationScreenBaseProps) => {
  return (
    <div className="relative min-h-screen overflow-hidden bg-white">
      <img
        src={logoSecondLane}
        alt="SecondLane"
        className="absolute left-11 top-6 h-6 w-24"
      />

      <div className="absolute right-0 top-0 hidden h-full w-[832px] lg:block">
        <img
          src={signupRightPanel}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>

      <div className="absolute left-[calc(4.17%+41px)] top-1/2 w-[654px] -translate-y-1/2 px-6 pb-8 pt-7">
        <div className="flex flex-col items-center gap-12">
          <div className="text-center">
            <h1 className="font-plus-jakarta text-[30px] font-medium leading-[40px] tracking-[-0.28px] text-[#2d2923]">
              Account Verification Required
            </h1>
            <p className="font-plus-jakarta text-[16px] font-normal leading-[24px] text-[rgba(45,41,35,0.64)]">
              Please complete the following attestation to access your account
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 border border-red-500 bg-red-500/10 px-4 py-3 text-red-500">
                <AlertCircle size={18} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-5">
              <h3 className="font-plus-jakarta text-[16px] font-medium leading-[24px] text-[#2d2923]">
                Income Verification
              </h3>

              <div className="space-y-4">
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    id="individual_income_test"
                    checked={formData.individual_income_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ individual_income_test: checked as boolean })
                    }}
                    className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:border-[rgba(255,255,255,0.82)]"
                  />
                  <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                    Does your annual individual income exceed $200,000?
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    id="joint_income_test"
                    checked={formData.joint_income_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ joint_income_test: checked as boolean })
                    }}
                    className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:border-[rgba(255,255,255,0.82)]"
                  />
                  <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                    Does your annual joint household income exceed $300,000?
                  </span>
                </label>

                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    id="net_worth_test"
                    checked={formData.net_worth_test}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ net_worth_test: checked as boolean })
                    }}
                    className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:border-[rgba(255,255,255,0.82)]"
                  />
                  <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                    Does your have a net worth exceeding $1 million (excluding primary residence)?
                  </span>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              <label className="block font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                Country of Origin
              </label>
              <div className="relative">
                <img src={world2Icon} alt="" className="pointer-events-none absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2" />
                <Select
                  value={formData.country}
                  onValueChange={(value) => {
                    onFormDataChange({ country: value })
                  }}
                >
                  <SelectTrigger className="h-[52px] w-full border-[rgba(45,41,35,0.12)] bg-[rgba(255,255,255,0.52)] pl-[46px] pr-[18px] text-[14px] font-medium text-[rgba(45,41,35,0.72)]">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="!bg-white text-[#2d2923] border-[rgba(45,41,35,0.12)] shadow-lg">
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="United Arab Emirates">UAE</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="Brazil">Brazil</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="France">France</SelectItem>
                    <SelectItem value="Germany">Germany</SelectItem>
                    <SelectItem value="Hong Kong">Hong Kong</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="Japan">Japan</SelectItem>
                    <SelectItem value="Singapore">Singapore</SelectItem>
                    <SelectItem value="South Korea">South Korea</SelectItem>
                    <SelectItem value="Switzerland">Switzerland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {fieldErrors.country && (
                <p className="font-plus-jakarta text-[12px] leading-[16px] text-[#ff540c]">
                  {fieldErrors.country}
                </p>
              )}
            </div>

            <div className="space-y-4">
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox
                  id="market_insights_opt_in"
                  checked={formData.market_insights_opt_in}
                  onCheckedChange={(checked) => {
                    onFormDataChange({ market_insights_opt_in: checked as boolean })
                  }}
                  className="h-5 w-5 rounded-none border-2 border-[rgba(255,255,255,0.82)] data-[state=checked]:border-[rgba(255,255,255,0.82)]"
                />
                <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                  Receive monthly market insights
                </span>
              </label>

              <div className="space-y-0.5">
                <label className="flex cursor-pointer items-center gap-3">
                  <Checkbox
                    id="affirmation"
                    checked={formData.affirmation}
                    onCheckedChange={(checked) => {
                      onFormDataChange({ affirmation: checked as boolean })
                    }}
                    className="h-5 w-5 rounded-none border-2 border-[rgba(45,41,35,0.32)] bg-[rgba(255,255,255,0.56)] data-[state=checked]:border-[rgba(255,255,255,0.82)]"
                  />
                  <span className="font-plus-jakarta text-[14px] font-medium leading-[20px] text-[#2d2923]">
                    I affirm that the information provided is true and accurate
                  </span>
                </label>
                <p
                  className={`pl-8 font-plus-jakarta text-[12px] font-medium leading-[16px] ${
                    fieldErrors.affirmation ? 'text-[#ff540c]' : 'text-[rgba(45,41,35,0.72)]'
                  }`}
                >
                  {fieldErrors.affirmation || 'You must check this box to proceed'}
                </p>
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <Button
                type="submit"
                variant="primary"
                className="h-12 w-full rounded-none bg-[rgba(45,41,35,0.72)] font-plus-jakarta text-[14px] font-medium leading-[20px] text-white hover:bg-[rgba(45,41,35,0.72)] hover:text-white"
                isLoading={loading}
                disabled={!formData.affirmation || !formData.country}
              >
                SUBMIT ATTESTATION
              </Button>

              <p className="mx-auto max-w-[554px] text-center font-plus-jakarta text-[14px] font-medium leading-[20px] text-[rgba(45,41,35,0.72)]">
                This information is required for compliance purposes and to ensure proper access to our platform
              </p>

              <p className="text-center font-plus-jakarta text-[12px] leading-[16px] text-[rgba(45,41,35,0.56)]">
                {saveState === 'saving'
                  ? 'Saving your progress…'
                  : saveState === 'error'
                    ? 'We could not save your progress — you can still submit.'
                    : saveState === 'saved'
                      ? 'Progress saved. You can finish this later.'
                      : 'Your progress is saved automatically.'}
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
